import { Box, Button, Popover, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SkinTone, SkinToneOption } from '@appTypes/emoji';

interface SkinToneSelectorProps {
  skinToneOptions: SkinToneOption[];
  selectedSkinTone: SkinTone;
  onSkinToneChange: (skinTone: SkinTone) => void;
}

export const SkinToneSelector = ({
  skinToneOptions,
  selectedSkinTone,
  onSkinToneChange,
}: SkinToneSelectorProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Get the localized name using the translation key
  const getLocalizedName = (option: SkinToneOption) => {
    return t(option.nameKey, option.id);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectSkinTone = (skinTone: SkinTone) => {
    onSkinToneChange(skinTone);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const selectedOption = skinToneOptions.find((option) => option.id === selectedSkinTone);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        sx={{
          minWidth: 'auto',
          px: 1,
          py: 0.5,
          fontSize: '1.2rem',
          borderRadius: 2,
        }}
        title={t('selectSkinTone') || 'Select Skin Tone'}
      >
        {selectedOption?.emoji || '👋'}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: { xs: 'auto', sm: 200 }, maxWidth: '90vw' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
            {t('selectSkinTone') || 'Select Skin Tone'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {skinToneOptions.map((option) => (
              <Button
                key={option.id}
                variant={selectedSkinTone === option.id ? 'contained' : 'outlined'}
                onClick={() => handleSelectSkinTone(option.id)}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '1.5rem',
                  borderRadius: 2,
                }}
                title={getLocalizedName(option)}
              >
                {option.emoji}
              </Button>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
